import { memo } from 'react'
import { NavLink } from 'react-router-dom'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../components/ui/tooltip'
import { Badge } from '../../../components/ui/badge'
import { cn } from '../../../lib/utils'
import type { NavigationItem } from './types'

interface NavigationProps {
    items: NavigationItem[]
    collapsed: boolean
    pendingApprovals: number
    onNavigate?: () => void
}

const Navigation = memo(({ items, collapsed, pendingApprovals, onNavigate }: NavigationProps) => {
    const renderBadge = (itemId: NavigationItem['id']) => {
        if (itemId !== 'users' || pendingApprovals <= 0) {
            return null
        }

        const content = pendingApprovals > 99 ? '99+' : pendingApprovals.toString()

        return (
            <Badge
                variant="warning"
                className={cn(
                    'tw:ml-auto tw:flex tw:min-w-[2rem] tw:items-center tw:justify-center tw:rounded-full tw:border-0 tw:bg-amber-500/20 tw:px-2 tw:py-1 tw:text-[0.7rem] tw:font-semibold tw:text-amber-100',
                    collapsed && 'tw:absolute tw:-right-2 tw:-top-2 tw:h-5 tw:w-5 tw:rounded-full tw:px-0 tw:py-0',
                )}
                aria-label={`${content} pending approvals`}
            >
                {content}
            </Badge>
        )
    }

    return (
        <TooltipProvider>
            <nav aria-label="Primary" className="tw:flex-1 tw:overflow-y-auto tw:px-4">
                <ul
                    className={cn(
                        'tw:flex tw:flex-col tw:gap-1 tw:pb-6 tw:list-none tw:p-0 tw:m-0',
                        collapsed ? 'tw:items-center' : 'tw:items-stretch',
                    )}
                >
                    {items.map((item) => {
                        const Icon = item.icon

                        const link = (
                            <NavLink
                                key={item.id}
                                to={item.path}
                                onClick={onNavigate}
                                className={({ isActive }) =>
                                    cn(
                                        'tw:group tw:relative tw:flex tw:items-center tw:rounded-xl tw:py-2 tw:text-sm tw:font-medium tw:text-slate-300 tw:transition-colors focus-visible:tw:outline-none focus-visible:tw:ring-2 focus-visible:tw:ring-blue-500',
                                        collapsed
                                            ? 'tw:w-12 tw:justify-center tw:gap-0 tw:px-0'
                                            : 'tw:w-full tw:justify-start tw:gap-3 tw:px-3',
                                        isActive
                                            ? 'tw:bg-blue-600/20 tw:text-white'
                                            : 'hover:tw:bg-slate-800/70 hover:tw:text-white',
                                    )
                                }
                                aria-label={collapsed ? item.label : undefined}
                            >
                                {({ isActive }) => (
                                    <>
                                        <span
                                            className={cn(
                                                'tw:absolute tw:left-0 tw:top-1/2 tw:h-6 tw:w-1 tw:-translate-y-1/2 tw:rounded-full tw:transition-opacity tw:duration-200',
                                                isActive ? 'tw:opacity-100 tw:bg-blue-500' : 'tw:opacity-0',
                                            )}
                                            aria-hidden="true"
                                        />
                                        <span
                                            className={cn(
                                                'tw:flex tw:h-10 tw:w-10 tw:items-center tw:justify-center tw:rounded-xl tw:bg-slate-900/70 tw:text-slate-300 tw:transition-colors',
                                                collapsed && 'tw:mx-auto',
                                                isActive && 'tw:bg-blue-600/30 tw:text-blue-200',
                                            )}
                                            aria-hidden="true"
                                        >
                                            <Icon className="tw:h-5 tw:w-5" aria-hidden="true" />
                                        </span>
                                        {!collapsed && (
                                            <div className="tw:flex tw:flex-1 tw:flex-col tw:gap-0.5">
                                                <span className="tw:text-sm tw:font-medium tw:text-inherit">
                                                    {item.label}
                                                </span>
                                                <span className="tw:text-xs tw:text-slate-400">{item.description}</span>
                                            </div>
                                        )}
                                        {renderBadge(item.id)}
                                    </>
                                )}
                            </NavLink>
                        )

                        if (!collapsed) {
                            return <li key={item.id}>{link}</li>
                        }

                        return (
                            <li
                                key={item.id}
                                className={cn('tw:relative tw:w-full tw:justify-center', collapsed && 'tw:flex')}
                            >
                                <Tooltip disableHoverableContent={false} delayDuration={collapsed ? 150 : 300}>
                                    <TooltipTrigger asChild>{link}</TooltipTrigger>
                                    <TooltipContent side="right" align="start">
                                        <div className="tw:flex tw:flex-col tw:gap-1">
                                            <span className="tw:text-sm tw:font-medium tw:text-white">
                                                {item.label}
                                            </span>
                                            <span className="tw:text-xs tw:text-blue-100/90">{item.description}</span>
                                            {item.id === 'users' && pendingApprovals > 0 && (
                                                <span className="tw:text-[0.65rem] tw:font-semibold tw:text-amber-200">
                                                    {pendingApprovals > 99 ? '99+' : pendingApprovals} pending approvals
                                                </span>
                                            )}
                                        </div>
                                    </TooltipContent>
                                </Tooltip>
                            </li>
                        )
                    })}
                </ul>
            </nav>
        </TooltipProvider>
    )
})

Navigation.displayName = 'SidebarNavigation'

export default Navigation
